import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import AuthShell from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const TYPES = [
  { v: "individual", l: "Individual practitioner" }, { v: "clinic", l: "Clinic" },
  { v: "agency", l: "Agency" }, { v: "nonprofit", l: "Non-profit" }, { v: "program", l: "Program" },
];

export default function ProviderRegister() {
  const { providerRegister } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", contact_name: "", provider_type: "individual", email: "", password: "", accept_terms: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.accept_terms) { toast.error("Please accept the Provider Terms to continue."); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await providerRegister(form);
      toast.success("Welcome! Let's build your profile.");
      navigate("/provider/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="List your practice on KidPath" subtitle="Reach Ontario families looking for autism & neurodiversity support. Free to join.">
      <form onSubmit={submit} className="space-y-4" data-testid="provider-register-form">
        <div>
          <Label className="text-sm">Business / practice name</Label>
          <Input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Bright Steps Therapy" className="mt-1.5 h-11" data-testid="pr-business" required />
        </div>
        <div>
          <Label className="text-sm">Your name</Label>
          <Input value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Jane Smith" className="mt-1.5 h-11" data-testid="pr-contact" required />
        </div>
        <div>
          <Label className="text-sm">Provider type</Label>
          <Select value={form.provider_type} onValueChange={(v) => set("provider_type", v)}>
            <SelectTrigger className="mt-1.5 h-11" data-testid="pr-type"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-sm">Work email</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@practice.ca" className="mt-1.5 h-11" data-testid="pr-email" required />
        </div>
        <div>
          <Label className="text-sm">Password</Label>
          <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="At least 8 characters" className="mt-1.5 h-11" data-testid="pr-password" required />
        </div>
        <label className="flex items-start gap-3 cursor-pointer">
          <Checkbox checked={form.accept_terms} onCheckedChange={(v) => set("accept_terms", !!v)} className="mt-0.5" data-testid="pr-terms" />
          <span className="text-sm text-kid-muted">I agree to the <Link to="/terms-of-service" className="text-kid-teal font-semibold">Provider Terms</Link> and Marketplace Guidelines, and confirm the information I provide will be accurate.</span>
        </label>
        <Button type="submit" disabled={loading} className="w-full h-12 rounded-full text-base" data-testid="pr-submit">
          {loading ? "Creating account…" : "Create provider account"}
        </Button>
        <p className="text-center text-sm text-kid-muted">Already have an account? <Link to="/login" className="text-kid-teal font-semibold">Sign in</Link></p>
      </form>
    </AuthShell>
  );
}
